import axios from 'axios';
import { get } from 'svelte/store';
import { map, isEmpty } from 'lodash'
import { system_fonts, extension_fonts, local_fonts, detected_fonts, font_detection_in_progress } from '../stores'
import { toTitleCase } from './toTitleCase'
import UAParser from 'ua-parser-js'

const API_KEY = process.env.GOOGLE_FONTS_API_KEY;

// EXTENSION IDS
let edgeExtensionId = 'jnpiemcccmekekaadgbcmchlcnmobged'
let chromeExtensionId = 'plafaneablgcojpbaeefkmnheilloopl'
// let edgeExtensionId = 'fkhgidlofibamphinabalkmhmbjidbke' // Dev
// let chromeExtensionId = 'fkhgidlofibamphinabalkmhmbjidbke' // Dev

const uaParser = new UAParser()

// STARTS HERE WITH APP.JS SETTING THIS 
// AS A VALUE TO A PROMISE

export async function detectFonts() {
  
  console.log('Fonts - starting font detection')
  
  // Get system fonts first
  let system_fonts_response = await getSystemFonts()
  if(system_fonts_response) {
    console.log('Fonts - received system fonts')
    system_fonts.set(system_fonts_response)
  }

  // Detect browser environment
  let environment = detectEnvironment()
  console.log('Fonts - detetced environment: ' + environment)

  // Look for extension fonts
  let extension_fonts = undefined
  if (environment === 'chrome') extension_fonts = await getExtensionFonts(chromeExtensionId)
  if (environment === 'edge') extension_fonts = await getExtensionFonts(edgeExtensionId)
  if (extension_fonts) await parseExtensionFonts(extension_fonts)

  // Look for permission for local fonts
  console.log('Fonts - Local - checking for local font permission')
  if ('queryLocalFonts' in self) {
    const status = await navigator.permissions.query({ name: "local-fonts" })
    if (status.state === "granted") {
      console.log('Fonts - Local - permission granted for local fonts')
      sendPermissionEvent()
      
      // If page is not visible, wait for visibility before querying local fonts
      if (document.visibilityState === "hidden") {
        console.log('Fonts - Local - waiting for page to be visible')
        await new Promise(resolve => {
          const handleVisibility = async () => {
            if (document.visibilityState !== "hidden") {
              document.removeEventListener("visibilitychange", handleVisibility)
              try {
                console.log('Fonts - Local - page visible, querying local fonts')
                const query_response = await self.queryLocalFonts()
                if (query_response) {
                  await parseLocalFonts(query_response)
                  console.log('Fonts - Local - local fonts parsed after visibility change')
                }
              } catch (e) {
                console.warn(`Fonts - Local - local font access failed after visibility change: ${e.message}`)
              }
              resolve()
            }
          }
          document.addEventListener("visibilitychange", handleVisibility)
        })
      } else {
        // Page is visible, query fonts immediately
        try {
          console.log('Fonts - Local - page visible, querying local fonts')
          const query_response = await self.queryLocalFonts()
          if (query_response) {
            await parseLocalFonts(query_response)
            console.log('Fonts - Local - local fonts parsed')
          }
        } catch (e) {
          console.warn(`Fonts - Local - local font access failed: ${e.message}`)
        }
      }
    } else {
      console.log('Fonts - Local - permission not granted, status: ' + status.state)
    }
  } else {
    console.log('Fonts - Local - permissioned access for local fonts not supported')
  }

  // Font detection is complete
  font_detection_in_progress.set(false)
}

async function handleVisibilityChange() {
  if (document.visibilityState !== "hidden") {
    console.log("Fonts - page visible, querying local fonts")
    try {
      const query_response = await self.queryLocalFonts()
      if (query_response) {
        await parseLocalFonts(query_response)
      }
    } catch (e) {
      console.warn(`Fonts - Local - failed to query local fonts after visibility change: ${e.message}`)
    }
  }
}


async function getSystemFonts() {
  return new Promise(async (resolve) => {
    let OS = uaParser.getOS();
    let json_url;
    let json_response = {};

    if (OS.name === 'Mac OS') {
      json_url = '/assets/system_fonts/osx-parsed.json';
      console.log('Fonts - getting system fonts for Mac OS');
    } else if (OS.name === 'iOS') {
      json_url = '/assets/system_fonts/ios-parsed.json';
      console.log('Fonts - getting system fonts for iOS');
    } else if (OS.name === 'Windows') {
      let win11 = await detectWin11();
      if (win11) {
        json_url = '/assets/system_fonts/win11-parsed.json';
        console.log('Fonts - getting system fonts for Win 11');
      } else if (OS.version === '10') {
        json_url = '/assets/system_fonts/win10-parsed.json';
        console.log('Fonts - getting system fonts for Win 10');
      } else {
        json_url = '/assets/system_fonts/win7-parsed.json';
        console.log('Fonts - getting system fonts for Win 7');
      }
    } else if (OS.name === 'Android') {
      json_url = '/assets/system_fonts/android-parsed.json';
      console.log('Fonts - getting system fonts for Android');
    } else {
      console.error('Fonts - No system fonts known for this OS');
      resolve(json_response);
      return;
    }

    axios.get(json_url)
      .then(response => {
        json_response = response.data;
        resolve(json_response);
      })
      .catch(error => {
        console.error(error);
        resolve(json_response);
      });
  });
}


export function detectEnvironment() {
  let detectedBrowser = uaParser.getBrowser()
  let result = 'none'
  if (detectedBrowser.name === "Edge") result = 'edge'
  else if (detectedBrowser.name === "Safari") result = 'safari'
  else if (detectedBrowser.name === "Firefox") result = 'firefox'
  else result = 'chrome'
  return result
}

async function detectWin11() {
  return new Promise(resolve => {
    if (!(('userAgentData' in navigator))) resolve(false)
    navigator.userAgentData.getHighEntropyValues(["platformVersion"]).then(ua => {
      if (navigator.userAgentData.platform === "Windows") {
        const majorPlatformVersion = parseInt(ua.platformVersion.split('.')[0])
        if (majorPlatformVersion >= 13) {
          resolve(true)
        }
        else {
          resolve(false)
        }
      }
      else {
        resolve(false)
      }
    })
  })
}

// GET EXTENSION FONTS
function getExtensionFonts(extensionId) {
  console.log('Fonts - Extension - checking for extension fonts')
  return new Promise( resolve => {
    if (typeof chrome === 'undefined') resolve(false)
    if (typeof chrome.runtime === 'undefined' || isEmpty(chrome.runtime)) resolve(false)
    chrome.runtime.sendMessage(extensionId, "",
      response => {
        if (!response) {
          console.log('Fonts- Extension - cannot connect to extension')
          resolve(false) 
        }
        if(!chrome.runtime.lastError) {
          // log number of fonts received from extension
          console.log("Fonts - Extension - received fonts from extension", response.result.length)
          if (response.result && Array.isArray(response.result)) {
            resolve(response.result.map((font) => font.displayName));
          } else {
            console.log('Fonts - Extension - unexpected response format from extension');
            resolve(false);
          }
        }
      }
    )
  })
}

// PARSE EXTENSION FONTS
function parseExtensionFonts(font_names) {
  console.log('Fonts - Extension - parsing extension fonts')
  return new Promise( resolve => {
    let parsed_fonts = new Array()
    let systemFontNames = map(get(system_fonts), f => f.family_name)
    for (let i = 0; i < font_names.length; i++) {
      const font_name = font_names[i]
      let fobj = {}
      fobj.id = i
      fobj.display_name = font_name
      fobj.postscript_name = font_name
      fobj.family_name = font_name.replace(/^\s+|\s+$/g, '').replace(/[^\w\s]/g, '').replace(/\s+/g, ' ')
      fobj.selected = false
      fobj.source = 'extension'
      fobj.system = systemFontNames.includes(font_name)
      fobj.user = !fobj.system
      fobj.tagged = false
      fobj.saved = false
      fobj.categorized = false
      parsed_fonts.push(fobj)
    }
    extension_fonts.set(parsed_fonts)
    resolve(true)
  })
}

// PARSE LOCAL FONTS
// Remove whitespace from beginning and end of font family name
// Remove all non-alphanumeric characters from font family name
// Replace all double space with a single space in font family name
export function parseLocalFonts(query_response) {
  console.log('Fonts - Local - parsing local fonts ', query_response.length)
  return new Promise(resolve => {
    let parsed_fonts = new Array()
    let systemFontNames = map(get(system_fonts), f => f.postscript_name)
    query_response.forEach((metadata, index) => {
      let fobj = {}
      fobj.id = index
      fobj.display_name = metadata.fullName
      fobj.postscript_name = metadata.postscriptName
      fobj.family_name = metadata.family.replace(/^\s+|\s+$/g, '').replace(/[^\w\s]/g, '').replace(/\s+/g, ' ')
      const parsedStlyeVals = getFontStyleAsCssValue(metadata.style)
      fobj.style = parsedStlyeVals.style;
      fobj.weight = parsedStlyeVals.weight;
      fobj.stretch = parsedStlyeVals.stretch;
      fobj.selected = false
      fobj.source = 'local'
      fobj.system = systemFontNames.includes(fobj.postscript_name)
      fobj.user = !fobj.system
      fobj.tagged = false
      fobj.saved = false
      fobj.categorized = false
      parsed_fonts.push(fobj)
    })
    local_fonts.set(parsed_fonts)
    
    // Always switch to local fonts if we have them and they were queried after visibility change
    if (parsed_fonts.length > 0) {
      const currentDetectedFonts = get(detected_fonts)
      const currentSource = currentDetectedFonts[0]?.source || ''
      
      // Switch to local fonts if current fonts are from system or extension
      if (currentSource === 'system' || currentSource === 'extension') {
        detected_fonts.set(parsed_fonts)
        console.log('Fonts - Local - switched to local fonts (from ' + currentSource + ')')
      }
    }
    resolve(true)
  })
}

// FETCH AND PARSE GOOGLE FONTS
export async function fetchGoogleFonts(callbackFunction) {
  let weights = {
    100: 'Thin',
    200: 'ExtraLight',
    300: 'Light',
    regular: 'Regular',
    500: 'Medium',
    600: 'SemiBold',
    700: 'Bold',
    800: 'ExtraBold',
    900: 'Black',
  }

  const res = await fetch('https://www.googleapis.com/webfonts/v1/webfonts?key=' + API_KEY)
  const fetched_fonts = await res.json()
  if (res.ok) {
    let c = 0
    let parsed_fonts = []
    fetched_fonts.items.forEach(font => {
      font.variants.forEach( variant => {
        let fobj = {}
        fobj.id = c
        const matchedWeight = Object.keys(weights).find(w => variant.includes(w));
        fobj.weight = (matchedWeight && matchedWeight !== 'regular') ? Number(matchedWeight) : 400;
        fobj.weight_name = weights[fobj.weight]
        fobj.style = variant.includes('italic') ? 'italic' : ''
        fobj.display_name = font.family + (fobj.weight_name ? ' ' +  fobj.weight_name + " " + fobj.weight : '')  + ' ' + toTitleCase(fobj.style)
        fobj.postscript_name = font.family
        fobj.family_name = font.family
        fobj.url = font.files[variant].match('^http://') ? font.files[variant].replace(/^http:\/\//i, 'https://') : font.files[variant]
        fobj.tags = [font.category]
        fobj.tagged = true
        fobj.subsets = font.subsets
        fobj.loadurl = font.family + ':' + variant
        fobj.loaded = false
        fobj.selected = false
        fobj.source = 'google'
        c++
        parsed_fonts.push(fobj)
      })
    })
    callbackFunction(parsed_fonts)
  } 
  else {
    throw new Error(fonts)
  }
}

// Convert font style string from local font access api to css properties
export function getFontStyleAsCssValue(styleStr) {
  const styleToPropsTable = {
    weight: [
      ["thin", 100],
      ["ultrathin", 100],
      ["ultra-thin", 100],
      ["hairline", 100],
      ["ultralight", 200],
      ["ultra-light", 200],
      ["extralight", 200],
      ["extra-light", 200],
      ["light", 300],
      ["semilight", 350],
      ["semi-light", 350],
      ["normal", 400],
      ["regular", 400],
      ["book", 400],
      ["text", 400],
      ["roman", 400],
      ["medium", 500],
      ["semibold", 600],
      ["semi-bold", 600],
      ["demibold", 600],
      ["demi-bold", 600],
      ["bold", 700],
      ["extrabold", 800],
      ["extra bold", 800],
      ["ultrabold", 800],
      ["ultra-bold", 800],
      ["heavy", 800],
      ["extraheavy", 900],
      ["extra-heavy", 900],
      ["black", 900],
      ["extrablack", 900],
      ["extra-black", 900],
      ["ultra", 900],
      ["ultrablack", 900],
      ["ultra-black", 900],
      ["100", 100],
      ["200", 200],
      ["300", 300],
      ["400", 400],
      ["500", 500],
      ["600", 600],
      ["700", 700],
      ["800", 800],
      ["900", 900]
    ],
    style: [
      ["italic", "italic"],
      ["ital", "italic"],
      ["slanted", "italic"],
      ["slnt", "italic"],
      ["cursive", "italic"],
      ["inclined", "italic"],
      ["incl", "italic"],
      ["sloped", "italic"],
      ["angled", "italic"],
      ["skewed", "italic"],
      ["oblique", "oblique"],
      ["obl", "oblique"],
      ["oblq", "oblique"]
    ],
    stretch: [
      ["condensed", "condensed"],
      ["cond", "condensed"],
      ["semi-condensed", "semi-condensed"],
      ["semicondensed", "semi-condensed"],
      ["semi-cond", "semi-condensed"],
      ["semicond", "semi-condensed"],
      ["extra-condensed", "extra-condensed"],
      ["extracondensed", "extra-condensed"],
      ["extra-cond", "extra-condensed"],
      ["extracond", "extra-condensed"],
      ["ultra-condensed", "ultra-condensed"],
      ["ultracondensed", "ultra-condensed"],
      ["ultra-cond", "ultra-condensed"],
      ["ultracond", "ultra-condensed"],
      ["expanded", "expanded"],
      ["exp", "expanded"],
      ["semi-expanded", "semi-expanded"],
      ["semiexpanded", "semi-expanded"],
      ["semi-exp", "semi-expanded"],
      ["semiexp", "semi-expanded"],
      ["extra-expanded", "extra-expanded"],
      ["extraexpanded", "extra-expanded"],
      ["extra-exp", "extra-expanded"],
      ["extraexp", "extra-expanded"],
      ["ultra-expanded", "ultra-expanded"],
      ["ultraexpanded", "ultra-expanded"],
      ["ultra-exp", "ultra-expanded"],
      ["ultraexp", "ultra-expanded"]
    ]
  };

  let propsObj = {};

  // Check for weight
  for (let i = 0; i < styleToPropsTable.weight.length; i++) {
    const _rgX = new RegExp(styleToPropsTable.weight[i][0], "i");
    if (styleStr.search(_rgX) !== -1) {
      propsObj.weight = styleToPropsTable.weight[i][1];
      break;
    }
  }

  // Check for style
  for (let i = 0; i < styleToPropsTable.style.length; i++) {
    const _rgX = new RegExp(styleToPropsTable.style[i][0], "i");
    if (styleStr.search(_rgX) !== -1) {
      propsObj.style = styleToPropsTable.style[i][1];
      break;
    }
  }

  // Check for stretch
  for (let i = 0; i < styleToPropsTable.stretch.length; i++) {
    const _rgX = new RegExp(styleToPropsTable.stretch[i][0], "i");
    if (styleStr.search(_rgX) !== -1) {
      propsObj.stretch = styleToPropsTable.stretch[i][1];
      break;
    }
  }

  return propsObj;
}



// SEND FONT PERMISSION EVENT TO ANALYTICS
function sendPermissionEvent() {
  if (typeof window.gtag == "undefined") return
  gtag('event', 'lfa_permission_exists');
}